import { useMemo, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Segmented,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Empty,
  Tooltip,
} from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { alertApi, type AlertHourlyRow } from '@/api/alert.api';

const { Title, Text } = Typography;

export function AlertStatsPage() {
  const [days, setDays] = useState<number>(30);

  const statsQuery = useQuery({
    queryKey: ['alert-stats', days],
    queryFn: () => alertApi.stats(days),
  });

  const checkinsQuery = useQuery({
    queryKey: ['alert-checkins', 50],
    queryFn: () => alertApi.checkins(50),
  });

  const dailyChartData = useMemo(
    () =>
      (statsQuery.data?.daily ?? []).map((r) => ({
        day: dayjs(r.day).format('MM/DD'),
        접속: r.checkins,
        기기: r.devices,
        IP: r.ips,
      })),
    [statsQuery.data],
  );

  // 0~23시를 모두 채워서 비는 시간대도 0으로 표시한다.
  const hourlyChartData = useMemo(() => {
    const byHour = new Map<number, AlertHourlyRow>(
      (statsQuery.data?.hourly ?? []).map((r) => [r.hour, r]),
    );
    return Array.from({ length: 24 }, (_, h) => ({
      hour: `${h}시`,
      접속: byHour.get(h)?.checkins ?? 0,
      기기: byHour.get(h)?.devices ?? 0,
    }));
  }, [statsQuery.data]);

  const totals = statsQuery.data?.totals;
  const allTime = statsQuery.data?.allTime;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          알람 앱 사용자
        </Title>
        <Segmented
          value={days}
          onChange={(v) => setDays(v as number)}
          options={[
            { label: '7일', value: 7 },
            { label: '30일', value: 30 },
            { label: '90일', value: 90 },
          ]}
        />
      </div>

      {statsQuery.isLoading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Tooltip title="기기별 익명 ID 기준 — 1.1.1부터 수집돼 이전 접속은 0으로 잡힙니다">
                  <Statistic title={`실사용자 · 기기 (${days}일)`} value={totals?.devices ?? 0} />
                </Tooltip>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title={`실사용자 · IP (${days}일)`} value={totals?.ips ?? 0} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title={`접속 수 (${days}일)`} value={totals?.checkins ?? 0} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="누적 기기 / IP / 접속"
                  value={`${allTime?.devices ?? 0} / ${allTime?.ips ?? 0} / ${allTime?.checkins ?? 0}`}
                />
              </Card>
            </Col>
          </Row>

          <Card title={`일자별 접속·실사용자 (${days}일)`} style={{ marginBottom: 16 }}>
            {dailyChartData.length === 0 ? (
              <Empty description="데이터 없음" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis allowDecimals={false} />
                  <ReTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="접속" stroke="#1A73E8" strokeWidth={2} />
                  <Line type="monotone" dataKey="기기" stroke="#34A853" strokeWidth={2} />
                  <Line type="monotone" dataKey="IP" stroke="#FBBC04" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card
            title={`시간대별 접속 — 몇 시에 앱을 여는지 (KST, ${days}일 합산)`}
            style={{ marginBottom: 16 }}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" interval={1} />
                <YAxis allowDecimals={false} />
                <ReTooltip />
                <Legend />
                <Bar dataKey="접속" fill="#1A73E8" />
                <Bar dataKey="기기" fill="#34A853" />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={10}>
              <Card title="버전별 접속">
                <Table
                  size="small"
                  pagination={false}
                  rowKey="version"
                  dataSource={statsQuery.data?.byVersion ?? []}
                  columns={[
                    {
                      title: '버전',
                      dataIndex: 'version',
                      render: (v: string) => <Tag>{v}</Tag>,
                    },
                    { title: '접속', dataIndex: 'checkins', align: 'right', width: 70 },
                    { title: '기기', dataIndex: 'devices', align: 'right', width: 70 },
                    { title: 'IP', dataIndex: 'ips', align: 'right', width: 70 },
                    {
                      title: '마지막 접속',
                      dataIndex: 'last_seen',
                      width: 130,
                      render: (v: string) => dayjs(v).format('MM/DD HH:mm'),
                    },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={14}>
              <Card title="최근 접속 50건">
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  loading={checkinsQuery.isLoading}
                  dataSource={checkinsQuery.data ?? []}
                  scroll={{ x: 'max-content', y: 400 }}
                  columns={[
                    {
                      title: '시각',
                      dataIndex: 'createdAt',
                      width: 140,
                      render: (v: string) => dayjs(v).format('MM/DD HH:mm:ss'),
                    },
                    {
                      title: '버전',
                      dataIndex: 'appVersion',
                      width: 90,
                      render: (v: string | null, row) => (
                        <Tooltip title={`빌드 ${row.buildNumber ?? '-'}`}>
                          <Tag>{v ?? '-'}</Tag>
                        </Tooltip>
                      ),
                    },
                    {
                      title: '기기',
                      dataIndex: 'deviceId',
                      width: 110,
                      render: (v: string | null) =>
                        v ? (
                          <Tooltip title={v}>
                            <Text code style={{ fontSize: 12 }}>
                              {v.slice(0, 8)}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text type="secondary">-</Text>
                        ),
                    },
                    {
                      title: 'IP',
                      dataIndex: 'ipAddress',
                      render: (v: string | null) => v || '-',
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
